import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, Activity, ChevronRight, Thermometer, ExternalLink, History, Briefcase, Ruler, Scale as WeightIcon } from 'lucide-react';
import { Language } from '../../types';
import { fetchPlayerStatsForModal, fetchMatchPlayerStats, fetchPlayerProfileForModal, fetchCoachProfile } from '../../services/footballService';
import { t } from '../../i18n';

interface PlayerQuickModalProps {
    isOpen: boolean;
    onClose: () => void;
    player: {
        id: string;
        name: string;
        number?: string;
        pos?: string;
        photo: string;
        teamName?: string;
        teamLogo?: string;
        fixtureId?: string; 
        isCoach?: boolean; 
    } | null;
    lang: Language;
}

const POSITION_MAP: Record<string, string> = {
    'G': 'Goalkeeper',
    'GK': 'Goalkeeper',
    'Goalkeeper': 'Goalkeeper',
    'D': 'Defender',
    'Defender': 'Defender',
    'M': 'Midfielder',
    'Midfielder': 'Midfielder',
    'A': 'Attacker',
    'Attacker': 'Attacker',
    'ST': 'Striker',
    'RW': 'Attacker',
    'LW': 'Attacker'
};

const getCountryCode = (nationality?: string) => {
    if (!nationality) return 'un';
    const map: Record<string, string> = {
        'Spain': 'es', 'England': 'gb-eng', 'Germany': 'de', 'France': 'fr',
        'Italy': 'it', 'Brazil': 'br', 'Argentina': 'ar', 'Portugal': 'pt',
        'Netherlands': 'nl', 'Belgium': 'be', 'Norway': 'no', 'Sweden': 'se',
        'Korea, South': 'kr', 'Japan': 'jp', 'USA': 'us', 'Poland': 'pl',
        'Turkey': 'tr', 'Morocco': 'ma', 'Croatia': 'hr', 'Denmark': 'dk',
        'Egypt': 'eg', 'Senegal': 'sn', 'Uruguay': 'uy', 'Colombia': 'co',
        'Nigeria': 'ng', 'Ghana': 'gh', 'Ivory Coast': 'ci', 'Cameroon': 'cm'
    };
    return map[nationality] || 'un';
};

export const PlayerQuickModal: React.FC<PlayerQuickModalProps> = ({ isOpen, onClose, player, lang }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [stats, setStats] = useState<any>(null);
    const [profile, setProfile] = useState<any>(null);
    const [coachData, setCoachData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsAnimating(true), 10);
            if (player?.id) {
                if (player.isCoach) {
                    loadCoachData();
                } else {
                    loadAllData();
                }
            }
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
            setStats(null);
            setProfile(null);
            setCoachData(null);
        }
    }, [isOpen, player?.id, player?.fixtureId, player?.isCoach]);

    const loadCoachData = async () => {
        if (!player?.id) return;
        setLoading(true);
        try {
            const data = await fetchCoachProfile(player.id);
            setCoachData(data);
        } finally {
            setLoading(false);
        }
    };

    const loadAllData = async () => {
        if (!player?.id) return;
        setLoading(true);
        
        try {
            const profilePromise = fetchPlayerProfileForModal(player.id);
            let statsPromise;

            if (player.fixtureId) {
                statsPromise = fetchMatchPlayerStats(player.fixtureId, player.id);
            } else {
                const currentYear = new Date().getFullYear();
                const season = new Date().getMonth() < 7 ? (currentYear - 1).toString() : currentYear.toString();
                statsPromise = fetchPlayerStatsForModal(player.id, season);
            }

            const [profileData, statsData] = await Promise.all([profilePromise, statsPromise]);
            
            let finalStats = statsData;
            if (player.fixtureId && !finalStats) {
                const currentYear = new Date().getFullYear();
                const season = new Date().getMonth() < 7 ? (currentYear - 1).toString() : currentYear.toString();
                finalStats = await fetchPlayerStatsForModal(player.id, season);
            }

            setProfile(profileData);
            setStats(finalStats);
        } finally {
            setLoading(false);
        }
    };

    const barData = useMemo(() => {
        if (!stats?.statistics?.[0]) return [];
        const s = stats.statistics[0];
        const isMatch = stats.isMatchSpecific;
        
    return [
        { label: t(lang, 'rating'), value: parseFloat(s.games?.rating || s.rating) || 0, color: '#f59e0b', max: 10 },
        { label: t(lang, 'goals'), value: s.goals?.total ?? 0, color: '#f43f5e', max: isMatch ? 3 : 20 },
        { label: t(lang, 'assists'), value: s.goals?.assists ?? 0, color: '#6366f1', max: isMatch ? 3 : 15 },
        { label: t(lang, 'passPercentage'), value: parseFloat(s.passes?.accuracy) || 0, color: '#10b981', max: 100 },
        { label: t(lang, 'dribbles'), value: s.dribbles?.success ?? 0, color: '#8b5cf6', max: isMatch ? 10 : 20 },
        { label: t(lang, 'defense'), value: (s.tackles?.total || 0) + (s.interceptions?.total || 0), color: '#64748b', max: isMatch ? 10 : 20 }
    ];
}, [stats, lang]);

    if (!isOpen || !player) return null;

    const handleClose = () => {
        setIsAnimating(false);
        setTimeout(onClose, 500);
    };

    const handleNavigate = () => {
        if (!player.isCoach) {
            if (window.navigateTo) {
                window.navigateTo(`/player/${player.id}`);
            } else {
                window.location.hash = `#/player/${player.id}`;
            }
            onClose();
        }
    };

    const pData = player.isCoach ? coachData : (profile?.player || stats?.player);
    const teamData = player.isCoach ? coachData?.team : (profile?.team || stats?.statistics?.[0]?.team);

    const isCaptain = stats?.statistics?.[0]?.games?.captain;
    const isInjured = !player.isCoach && pData?.injured;
    const birthDate = pData?.birth?.date;
    const nationality = pData?.nationality;
    const countryCode = getCountryCode(nationality);
    const isMatchData = stats?.isMatchSpecific;

    return (
        <div className={`fixed inset-0 z-[300] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={handleClose}></div>

            <div className={`relative bg-white w-full sm:max-w-lg rounded-t-[40px] sm:rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                
                <div className="absolute top-6 right-6 z-20">
                    <button onClick={handleClose} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all active:scale-90">
                        <X size={20} strokeWidth={3} />
                    </button>
                </div>

                <div className="p-8 pb-10">
                    <div 
                        className={`flex items-center gap-6 mb-8 mt-2 p-2 rounded-3xl transition-all ${player.isCoach ? 'cursor-default' : 'cursor-pointer group'}`}
                        onClick={handleNavigate}
                    >
                        <div className="relative shrink-0">
                            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-slate-100 bg-slate-100 shadow-sm md:group-hover:scale-105 transition-transform duration-500">
                                <img 
                                    src={player.photo} 
                                    alt={player.name} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.name)}&background=0f172a&color=fff&size=256`; }}
                                />
                            </div>
                            {isCaptain && (
                                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-black text-[10px] border-2 border-white shadow-sm ring-1 ring-amber-100" title={t(lang, 'captain')}>
                                    C
                                </div>
                            )}
                            {isInjured && (
                                <div className="absolute -top-1 -right-1 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm" title={t(lang, 'injured')}>
                                    <Thermometer size={14} strokeWidth={3} />
                                </div>
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase md:group-hover:text-rose-600 transition-colors truncate mb-1">
                                {player.name}
                            </h2>

                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-slate-500 font-bold text-xs md:text-[13px]">
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <img src={`https://flagcdn.com/w40/${countryCode}.png`} alt="" className="w-4 h-3 object-cover rounded-sm" />
                                    <span className="text-blue-600 font-black uppercase">{player.isCoach ? t(lang, 'coach') : t(lang, player.pos?.toLowerCase() || '') || player.pos}</span>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <span className="text-slate-900">{pData?.age || '-'}</span>
                                    {birthDate && (
                                        <span className="text-slate-400 font-medium text-[10px]">({birthDate})</span>
                                    )}
                                </div>
                                
                                {!player.isCoach && (pData?.height || pData?.weight) && (
                                    <div className="flex items-center gap-3 md:ml-1 md:border-l border-slate-100 md:pl-3">
                                        {pData?.height && (
                                            <div className="flex items-center gap-1 text-[11px]">
                                                <Ruler size={12} className="text-slate-300" />
                                                <span className="text-slate-700 tabular-nums">{pData.height}cm</span>
                                            </div>
                                        )}
                                        {pData?.weight && (
                                            <div className="flex items-center gap-1 text-[11px]">
                                                <WeightIcon size={12} className="text-slate-300" />
                                                <span className="text-slate-700 tabular-nums">{pData.weight}kg</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {teamData?.name && (
                                <div className="flex items-center gap-2 mt-1.5">
                                    <img src={teamData.logo} className="w-4 h-4 object-contain" alt="" />
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">{teamData.name}</span>
                                </div>
                            )}
                        </div>
                        
                        {!player.isCoach && (
                            <div className="pr-2 transition-all md:group-hover:translate-x-1">
                                <ChevronRight size={24} className="text-slate-300" strokeWidth={3} />
                            </div>
                        )}
                    </div>

                    <div className="relative min-h-[220px] flex flex-col">
                        {loading ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                                <Loader2 className="animate-spin text-rose-500" size={32} />
                                <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">{t(lang, 'retrievingIntel')}</span>
                            </div>
                        ) : player.isCoach ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Briefcase size={14} className="text-slate-400" />
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'managerialCareer')}</span>
                                </div>
                                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                    {coachData?.career && coachData.career.length > 0 ? coachData.career.map((c: any, i: number) => (
                                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100 group">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 shrink-0">
                                                    <img src={c.team.logo} className="w-full h-full object-contain" alt="" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-black text-slate-900 truncate uppercase leading-none mb-1">{c.team.name}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                                                        {c.start} — {c.end || t(lang, 'present')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center py-10 text-slate-300 font-bold uppercase text-[10px]">{t(lang, 'careerIntelNotAvailable')}</div>
                                    )}
                                </div>
                            </div>
                        ) : stats ? (
                            <div className="w-full space-y-4">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 text-center">
                                    {isMatchData ? t(lang, 'singleMatchPerformance') : t(lang, 'seasonIntelPerformance')}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                    {barData.map((data, i) => (
                                        <div key={i} className="space-y-1.5">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight">{data.label}</span>
                                                <span className="text-[11px] font-black text-slate-900 tabular-nums">
                                                    {data.label === 'Rating' ? data.value.toFixed(1) : data.value}
                                                </span>
                                            </div>
                                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full rounded-full transition-all duration-1000 ease-out"
                                                    style={{ 
                                                        width: `${Math.min(100, (data.value / data.max) * 100)}%`,
                                                        backgroundColor: data.color 
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center text-slate-200 py-10">
                                <Activity size={40} strokeWidth={1} />
                                <span className="text-[10px] font-black uppercase mt-2">{t(lang, 'noStatsSynced')}</span>
                            </div>
                        )}
                    </div>
                </div>
                
            </div>
        </div>
    );
};
